import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { products, categories, siteImages } from '../data/mock';
import { ChevronDown, LayoutGrid, Check } from 'lucide-react';

const CatButton = ({ active, onClick, label, count }) => (
  <button onClick={onClick} className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-athletic-600 text-white' : 'text-dark-700 hover:bg-white hover:text-athletic-700'}`}>
    <span className="flex items-center gap-2">{active && <Check className="w-4 h-4" />}{label}</span>
    <span className={`text-xs ${active ? 'text-white/80' : 'text-dark-400'}`}>{count}</span>
  </button>
);

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('featured');
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => { document.title = 'Shop Office Supplies & Workspace Essentials | Desk Line Supply'; }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    if (selectedCategory) result = result.filter(p => p.categorySlug === selectedCategory);
    switch (sortBy) {
      case 'price-low': result.sort((a, b) => a.price - b.price); break;
      case 'price-high': result.sort((a, b) => b.price - a.price); break;
      case 'name': result.sort((a, b) => a.name.localeCompare(b.name)); break;
      default: break;
    }
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (slug) => { if (slug) setSearchParams({ category: slug }); else setSearchParams({}); };
  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Banner (light) */}
        <section className="relative bg-dark-900 overflow-hidden">
          <img src={siteImages.shopBanner} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/85 to-transparent" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <nav className="text-sm text-dark-300 mb-3"><Link to="/" className="hover:text-white">Home</Link><span className="mx-2">/</span><span className="text-white">{currentCategory ? currentCategory.name : 'Shop All'}</span></nav>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-white">{currentCategory ? currentCategory.name : 'The Office Supply Aisle'}</h1>
            <p className="mt-3 text-dark-200 max-w-2xl">{currentCategory ? currentCategory.description : 'A carefully curated collection of office supplies and workspace essentials \u2014 organization, writing, electronics, furniture and decor for a desk that just works.'}</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-[240px_1fr] gap-8">
            {/* Sidebar */}
            <aside className="lg:sticky lg:top-28 self-start">
              <div className="bg-dark-50 rounded-xl border border-dark-100 p-3">
                <p className="px-4 pt-2 pb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-dark-400">Categories</p>
                <div className="space-y-1">
                  <CatButton active={!selectedCategory} onClick={() => handleCategoryChange('')} label="All Products" count={products.length} />
                  {categories.map((cat) => (
                    <CatButton key={cat.id} active={selectedCategory === cat.slug} onClick={() => handleCategoryChange(cat.slug)} label={cat.name} count={cat.productCount} />
                  ))}
                </div>
              </div>
              <div className="mt-4 bg-gradient-athletic rounded-xl p-5 text-white hidden lg:block">
                <p className="font-display text-lg font-semibold">Free USA shipping</p>
                <p className="text-white/85 text-sm mt-1">On every order, no minimum. Delivered by USPS in 2&ndash;4 days.</p>
              </div>
            </aside>

            {/* Main */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-6">
                <p className="flex items-center gap-2 text-sm text-dark-600"><LayoutGrid className="w-4 h-4 text-athletic-600" /><span className="font-semibold text-dark-900">{filteredProducts.length}</span> product{filteredProducts.length !== 1 ? 's' : ''}</p>
                <div className="relative">
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="appearance-none bg-white border border-dark-200 text-dark-700 text-sm rounded-lg px-5 py-2.5 pr-9 focus:outline-none focus:ring-2 focus:ring-athletic-500 cursor-pointer">
                    <option value="featured">Featured</option><option value="price-low">Price: Low to High</option><option value="price-high">Price: High to Low</option><option value="name">Name: A-Z</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                </div>
              </div>
              {filteredProducts.length === 0 ? (
                <div className="bg-dark-50 rounded-xl p-12 text-center border border-dark-100"><p className="text-dark-600">No products found in this category.</p></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">{filteredProducts.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ShopPage;
